import { useState } from 'react'
import { useQueryClient } from 'react-query'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Add as AddIcon } from 'almanac-ui/icons'
import { sendToApi } from 'api/client'
import { ConfirmModal } from 'hooks/useConfirmModal'
import { APICredential } from 'types'
import { useApiKeys } from './hooks/queries/useApiKeys'
import { useTextClipboard } from './hooks/useTextClipboard'
import Styles from './ApiKey.module.scss'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Button } from 'blaze-ui/Button/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Copy, EyeOpen, Trash2 } from 'blaze-ui/icons'

export const ApiKeySection = (): JSX.Element => {
  return (
    <div className={Styles.apiKeySectionContainer}>
      <Heading level={5}>Blaze API Key for Zapier</Heading>
      <div className={Styles.createAndDisclaimerContainer}>
        <AddAPIKey />
        <p className={Styles.disclaimer}>Generate more API keys to integrate with other apps</p>
      </div>
      <APIKeyList />
    </div>
  )
}

/* NOTE: Moving these components to individual files just exposes code that is not reusable
         and complicates refactoring for no real benefit */
const APIKeyList = (): JSX.Element => {
  const apiKeys = useApiKeys()
  if (apiKeys.isLoading) {
    return <div>Loading...</div>
  }
  if (apiKeys.error) {
    return (
      <p className={Styles.failedToLoad}>
        We had an error loading the API credentials, our team has been notified, please try again later
      </p>
    )
  }
  return (
    <>
      <div className={Styles.apiKeyList}>
        {apiKeys.data?.api_credentials
          .filter((cred) => cred.intended_use !== 'zapier')
          .map((cred) => (
            <APIKey key={cred.id} {...cred} />
          ))}
      </div>
    </>
  )
}

const AddAPIKey = (): JSX.Element => {
  const queryClient = useQueryClient()

  const onAdd = async () => {
    await sendToApi<APICredential[]>(`/api-credentials/`, { method: 'POST' })
    queryClient.invalidateQueries(['api-credentials'])
  }
  return (
    <Button frontIcon={AddIcon} onPress={onAdd} size="md" variant="secondary">
      New Key
    </Button>
  )
}

export const APIKey = (props: APICredential & { showLabel?: boolean; hideDelete?: boolean }): JSX.Element => {
  const [locked, setLocked] = useState<boolean>(true)
  const queryClient = useQueryClient()
  const { openModal, closeModal } = useModals()
  const copyToClipboard = useTextClipboard()
  const { isMobile } = useResponsiveDesign()

  const deleteApiKey = () => {
    if (props.hideDelete) return

    openModal(
      DeleteApiCredentialConfirmationModal,
      {
        onConfirm: async () => {
          closeModal()
          await sendToApi<APICredential[]>(`/api-credentials/${props.id}`, { method: 'DELETE' })
          queryClient.invalidateQueries(['api-credentials'])
        },
        onCancel: () => {
          closeModal()
        },
        onPressOutside: () => {
          closeModal()
        },
      },
      {
        newStack: false,
      },
    )
  }

  const onCopy = () => copyToClipboard(props.secret)

  return (
    <div className={Styles.apiKey}>
      <div className={Styles.inputContainer}>
        {props.showLabel && <p className={Styles.label}>Blaze API key</p>}
        <div className={Styles.inputRow}>
          <input readOnly type={locked ? 'password' : 'text'} value={props.secret} className={Styles.apiCredValue} />
          {isMobile && (
            <Button
              title={locked ? 'Show secret' : 'Hide secret'}
              preventFocusOnPress
              size="md"
              frontIcon={EyeOpen}
              variant="tertiary"
              onPress={() => setLocked(!locked)}
            />
          )}
        </div>
      </div>
      <div className={Styles.apiKeyActionsContainer}>
        {props.showLabel && <div className={Styles.labelPlaceholder}></div>}
        <div className={Styles.apiKeyActions}>
          <Button
            title="Copy to clipboard"
            preventFocusOnPress
            size="md"
            fullWidth={isMobile}
            frontIcon={Copy}
            variant="tertiary"
            onPress={onCopy}
          >
            Copy
          </Button>
          {!isMobile && (
            <Button
              title={locked ? 'Show secret' : 'Hide secret'}
              preventFocusOnPress
              size="md"
              frontIcon={EyeOpen}
              variant="tertiary"
              onPress={() => setLocked(!locked)}
            />
          )}
          {/* TODO: Figure out a way to make this modal stack, not transition */}
          {!props.hideDelete && (
            <Button
              preventFocusOnPress
              fullWidth={isMobile}
              size="md"
              title={'Delete key'}
              frontIcon={Trash2}
              variant="tertiary"
              onPress={() => deleteApiKey()}
            >
              {isMobile ? 'Delete API Key' : ''}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

const DeleteApiCredentialConfirmationModal = ({ onConfirm, onCancel }: ConfirmModal) => {
  return (
    <Modal.Root>
      <Modal.Header title="Delete API Credential" />
      <Modal.Content>
        <p>Any external integrations using this key won't work anymore.</p>
        <p>Are you sure you want to continue?</p>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button title="Cancel API credential deletion" size="lg" variant="tertiary" onPress={onCancel}>
            Cancel
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button title="Confirm API credential deletion" size="lg" onPress={onConfirm}>
            Delete
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
